import {PowerStationParams} from "../../../../Shared/Types/PowerStation";

export const MAX_DATA_QUALITY_THRESHOLD = 100;
// Os intervalos são Integer no backend; acima disso a requisição falha na desserialização.
export const MAX_COLLECT_INTERVAL = 2_147_483_647;

export const REQUIRED_FIELD_MESSAGE = "Campo Obrigatório.";
export const INVALID_INTERVAL_MESSAGE = "Valor inválido. Informe um número inteiro maior que zero.";
export const INTERVAL_ABOVE_MAX_MESSAGE = "Valor acima do máximo permitido.";
export const INVALID_DATA_QUALITY_THRESHOLD_MESSAGE =
    "Valor inválido. Informe um número entre 0 e 100 com até duas casas decimais.";

const NUMBER_PATTERN = /^-?\d+([.,]\d+)?$/;
const INTEGER_PATTERN = /^-?\d+$/;

/**
 * Converte o texto digitado no campo para o valor enviado ao backend. Campo vazio vira null e
 * texto que não é número vira NaN, para que a validação do salvamento o recuse.
 */
export const parseParamText = (text: string): number | null => {
    const trimmed = text.trim();
    if (trimmed === "") {
        return null;
    }
    return NUMBER_PATTERN.test(trimmed) ? Number(trimmed.replace(",", ".")) : NaN;
};

/**
 * Como parseParamText, mas sem aceitar separador: nos intervalos "1.000" é ambíguo (mil no
 * padrão brasileiro, um no americano) e não pode ser gravado como 1.
 */
export const parseIntervalText = (text: string): number | null => {
    const trimmed = text.trim();
    if (trimmed === "") {
        return null;
    }
    return INTEGER_PATTERN.test(trimmed) ? Number(trimmed) : NaN;
};

export const toParamText = (value: number | null | undefined): string =>
    value == null ? "" : String(value).replace(".", ",");

const isValidInterval = (value: number): boolean =>
    Number.isInteger(value) && value > 0 && value <= MAX_COLLECT_INTERVAL;

const hasAtMostTwoDecimals = (value: number): boolean => Number(value.toFixed(2)) === value;

const isValidDataQualityThreshold = (value: number): boolean =>
    value >= 0 && value <= MAX_DATA_QUALITY_THRESHOLD && hasAtMostTwoDecimals(value);

export const getIntervalError = (text: string, required: boolean): string | undefined => {
    const value = parseIntervalText(text);
    if (value === null) {
        return required ? REQUIRED_FIELD_MESSAGE : undefined;
    }
    if (Number.isInteger(value) && value > MAX_COLLECT_INTERVAL) {
        return INTERVAL_ABOVE_MAX_MESSAGE;
    }
    return isValidInterval(value) ? undefined : INVALID_INTERVAL_MESSAGE;
};

export const getDataQualityThresholdError = (text: string): string | undefined => {
    const value = parseParamText(text);
    if (value === null || isValidDataQualityThreshold(value)) {
        return undefined;
    }
    return INVALID_DATA_QUALITY_THRESHOLD_MESSAGE;
};

export const hasValidCollectIntervals = (params: PowerStationParams): boolean =>
    params.meterInterval != null && isValidInterval(params.meterInterval)
    && params.inverterInterval != null && isValidInterval(params.inverterInterval)
    && (params.solarimetricInterval == null || isValidInterval(params.solarimetricInterval));

export const hasValidDataQualityThreshold = (params: PowerStationParams): boolean =>
    params.dataQualityThreshold == null || isValidDataQualityThreshold(params.dataQualityThreshold);
